/**
 * MATLAB classdef / OOP Runtime
 *
 * Supports:
 * - classdef with properties and methods blocks
 * - Constructor (same name as class)
 * - Property access (obj.prop) and assignment (obj.prop = val)
 * - Method calls (obj.method(args))
 * - Inheritance (< superclass)
 * - Static methods
 * - Property validation (simple type/size checks)
 * - Value classes (default) and handle classes
 */
import { MatlabEvaluator } from "./evaluator";
let nextObjectId = 1;
// ============================================================
// Class registry
// ============================================================
const classRegistry = new Map();
/**
 * Register a class definition
 */
export function registerClass(def) {
    classRegistry.set(def.name, def);
}
/**
 * Get a class definition by name
 */
export function getClass(name) {
    return classRegistry.get(name);
}
/**
 * Check if a class exists
 */
export function hasClass(name) {
    return classRegistry.has(name);
}
/**
 * List all registered classes
 */
export function listClasses() {
    return Array.from(classRegistry.keys());
}
// ============================================================
// Object construction
// ============================================================
/**
 * Create a new instance of a class
 */
export function constructObject(className, args, env) {
    const classDef = classRegistry.get(className);
    if (!classDef) {
        throw new Error(`Undefined class: ${className}`);
    }
    // Initialize property values from defaults
    const propValues = new Map();
    // Walk inheritance chain (depth-first)
    const initFromClass = (cdef) => {
        for (const superName of cdef.superclasses) {
            const superDef = classRegistry.get(superName);
            if (superDef) {
                initFromClass(superDef);
            }
        }
        for (const [name, prop] of cdef.properties) {
            propValues.set(name, prop.defaultValue);
        }
    };
    initFromClass(classDef);
    const obj = {
        type: "object",
        className,
        properties: propValues,
        classDef,
        id: nextObjectId++,
    };
    // Call constructor if defined
    const constructor = getMethod(classDef, className);
    if (constructor) {
        callMethod(obj, className, args, env);
    }
    return obj;
}
// ============================================================
// Property access
// ============================================================
/**
 * Get a property value from an object
 */
export function getProperty(obj, name, env) {
    const propDef = getPropertyDef(obj.classDef, name);
    // If property not found, check for method (function handle)
    if (!propDef) {
        const method = getMethod(obj.classDef, name);
        if (method) {
            // Return a callable handle
            return {
                type: "handle",
                name: `${obj.className}.${name}`,
            };
        }
        throw new Error(`No property '${name}' in class ${obj.className}`);
    }
    // Dependent property — call getter
    if (propDef.dependent && propDef.getMethod) {
        const getter = getMethod(obj.classDef, propDef.getMethod);
        if (getter && env) {
            return callMethod(obj, propDef.getMethod, [], env);
        }
    }
    return obj.properties.get(name) ?? propDef.defaultValue;
}
/**
 * Set a property value on an object
 */
export function setProperty(obj, name, value, env) {
    const propDef = getPropertyDef(obj.classDef, name);
    if (!propDef) {
        throw new Error(`No property '${name}' in class ${obj.className}`);
    }
    if (propDef.constant) {
        throw new Error(`Cannot modify Constant property '${name}'`);
    }
    // Call setter if defined
    if (propDef.setMethod) {
        const setter = getMethod(obj.classDef, propDef.setMethod);
        if (setter && env) {
            callMethod(obj, propDef.setMethod, [value], env);
            return;
        }
    }
    obj.properties.set(name, value);
}
// ============================================================
// Method dispatch
// ============================================================
/**
 * Find a method in the class or superclass chain
 */
export function getMethod(classDef, name) {
    if (classDef.methods.has(name)) {
        return classDef.methods.get(name);
    }
    // Check superclasses
    for (const superName of classDef.superclasses) {
        const superDef = classRegistry.get(superName);
        if (superDef) {
            const method = getMethod(superDef, name);
            if (method)
                return method;
        }
    }
    return undefined;
}
/**
 * Find a property definition in class or superclass chain
 */
function getPropertyDef(classDef, name) {
    if (classDef.properties.has(name)) {
        return classDef.properties.get(name);
    }
    for (const superName of classDef.superclasses) {
        const superDef = classRegistry.get(superName);
        if (superDef) {
            const prop = getPropertyDef(superDef, name);
            if (prop)
                return prop;
        }
    }
    return undefined;
}
/**
 * Call a method on an object
 */
export function callMethod(obj, methodName, args, env) {
    const method = getMethod(obj.classDef, methodName);
    if (!method) {
        throw new Error(`No method '${methodName}' in class ${obj.className}`);
    }
    const childEnv = env.createChild();
    if (method.isStatic) {
        // Static methods don't receive obj as first arg
        for (let i = 0; i < method.params.length; i++) {
            childEnv.set(method.params[i], args[i] ?? 0);
        }
    }
    else {
        // Instance method — first param is 'obj' (or whatever name is used)
        if (method.params.length > 0) {
            childEnv.set(method.params[0], obj);
        }
        for (let i = 1; i < method.params.length; i++) {
            childEnv.set(method.params[i], args[i - 1] ?? 0);
        }
    }
    // nargin / nargout
    childEnv.set("nargin", method.isStatic ? args.length : args.length + 1);
    childEnv.set("nargout", method.outputs.length || 1);
    // Initialize output variables
    for (const out of method.outputs) {
        childEnv.set(out, 0);
    }
    const evaluator = new MatlabEvaluator(childEnv);
    try {
        evaluator.evaluate(method.body);
    }
    catch (e) {
        // ReturnSignal is expected
        if (!(e && typeof e === "object" && "values" in e)) {
            throw e;
        }
    }
    // Collect outputs
    const outputs = method.outputs.map((name) => childEnv.get(name) ?? 0);
    // If this is a constructor, copy property values back to obj
    if (methodName === obj.className) {
        const constructedObj = childEnv.get(method.params[0]);
        if (constructedObj && constructedObj.type === "object") {
            for (const [k, v] of constructedObj.properties) {
                obj.properties.set(k, v);
            }
        }
    }
    return outputs.length === 1 ? outputs[0] : outputs;
}
/**
 * Call a static method
 */
export function callStaticMethod(className, methodName, args, env) {
    const classDef = classRegistry.get(className);
    if (!classDef) {
        throw new Error(`Undefined class: ${className}`);
    }
    const method = getMethod(classDef, methodName);
    if (!method) {
        throw new Error(`No method '${methodName}' in class ${className}`);
    }
    if (!method.isStatic) {
        throw new Error(`Method '${methodName}' is not static`);
    }
    const childEnv = env.createChild();
    for (let i = 0; i < method.params.length; i++) {
        childEnv.set(method.params[i], args[i] ?? 0);
    }
    for (const out of method.outputs) {
        childEnv.set(out, 0);
    }
    const evaluator = new MatlabEvaluator(childEnv);
    try {
        evaluator.evaluate(method.body);
    }
    catch {
        // ReturnSignal expected
    }
    const outputs = method.outputs.map((name) => childEnv.get(name) ?? 0);
    return outputs.length === 1 ? outputs[0] : outputs;
}
// ============================================================
// Type checking utilities
// ============================================================
/**
 * Check if a value is a MatlabObject
 */
export function isMatlabObject(v) {
    return (typeof v === "object" &&
        v !== null &&
        "type" in v &&
        v.type === "object");
}
/**
 * Check if object is instance of a class (including superclasses)
 */
export function isInstanceOf(obj, className) {
    if (obj.className === className)
        return true;
    const checkSuper = (cdef) => {
        for (const superName of cdef.superclasses) {
            if (superName === className)
                return true;
            const superDef = classRegistry.get(superName);
            if (superDef && checkSuper(superDef))
                return true;
        }
        return false;
    };
    return checkSuper(obj.classDef);
}
/**
 * disp() representation of an object
 */
export function objectToString(obj) {
    const lines = [`  ${obj.className} with properties:\n`];
    const maxLen = Math.max(...Array.from(obj.properties.keys()).map((k) => k.length), 0);
    for (const [name, value] of obj.properties) {
        const padding = " ".repeat(maxLen - name.length + 4);
        const valStr = typeof value === "number"
            ? String(value)
            : typeof value === "string"
                ? `'${value}'`
                : Array.isArray(value)
                    ? `[${Array.isArray(value[0]) ? `${value.length}x${value[0].length}` : `1x${value.length}`}]`
                    : String(value);
        lines.push(`    ${name}${padding}${valStr}`);
    }
    return lines.join("\n");
}
// ============================================================
// Built-in class helpers for builtins.ts
// ============================================================
/**
 * Create a simple ClassDef from a definition object
 */
export function defineClass(opts) {
    const properties = new Map();
    for (const p of opts.properties || []) {
        properties.set(p.name, {
            name: p.name,
            defaultValue: p.default ?? 0,
            access: p.access || "public",
            constant: p.constant || false,
            dependent: false,
        });
    }
    const methods = new Map();
    for (const m of opts.methods || []) {
        methods.set(m.name, {
            name: m.name,
            params: m.params,
            outputs: m.outputs || [],
            body: m.body,
            access: m.access || "public",
            isStatic: m.isStatic || false,
            isAbstract: false,
        });
    }
    const classDef = {
        type: "classdef",
        name: opts.name,
        superclasses: opts.superclasses || [],
        isHandle: opts.isHandle || false,
        isSealed: false,
        properties,
        methods,
        events: [],
        enumMembers: new Map(),
    };
    registerClass(classDef);
    return classDef;
}
//# sourceMappingURL=classdef.js.map